import { useEffect, useMemo, useState } from "react";
import useAuth from "./useAuth";
import { db } from "../firebaseConfig";
import { realEstateContactConverter } from "../utils/firestoreConverters";
import { CONTACT_DOCUMENT_SOURCE } from "./contacts/contactDocumentSource";
import { useContactDocumentShadow } from "./contacts/contactDocumentShadow";
import useContactBook from "./contacts/useContactBook";
import useContactDocument from "./contacts/useContactDocument";
import type { ContactReadOptions } from "./contacts/contactDocumentRules";
import type { RealEstateContact } from "../@types/sharedSchema";
import { createLiveContactStore } from "./contacts/liveContactStore";
import type { LiveContactSnapshot } from "./contacts/liveContactStore";

const liveContacts = createLiveContactStore((userEmail, contactId, onContact, onError) =>
  db
    .collection("dashboard-users-v2")
    .doc(userEmail)
    .collection("contacts")
    .doc(contactId)
    .withConverter(realEstateContactConverter)
    .onSnapshot(
      (snap) => onContact(snap.exists ? (snap.data() as RealEstateContact) : null),
      (error) => {
        console.warn(`useLiveContact: subscription failed for ${contactId}:`, error);
        onError(error as Error);
      }
    )
);

const IDLE: LiveContactSnapshot = { contact: null, loading: false, error: null };

/**
 * ONE contact document under the authenticated user, kept live.
 *
 * Path: `dashboard-users-v2/{user.email}/contacts/{contactId}`
 *
 * Returns the live data so chat components reflect external edits (other
 * tab, mobile app, server webhook) without waiting for a manual refresh.
 *
 * The `contactId` argument is the Firestore doc id (which, in this codebase,
 * happens to be the contact's international phone number — but callers
 * should not depend on that). When `contactId` is null/undefined the hook
 * is a no-op.
 *
 * It is addressed by DOCUMENT ID. A surface that holds a phone in some spelling
 * (a chat card, a call, a calendar attendee) wants `useContactByPhone`, which
 * finds the document under whichever id it was stored as; a surface that only
 * needs a name beside a phone wants `useContactsLookup`. None of the three
 * reads `useLegacyContactsBook().allContacts`, the whole-book array.
 *
 * Mounted readers of the same (account, document id) share the snapshot and listener.
 * A sidebar → page handoff therefore renders the already-live contact on its FIRST
 * render. Switching ids/accounts reads the new key immediately, never the old state.
 *
 * ── Which mechanism answers: `CONTACT_DOCUMENT_SOURCE` ──────────────────────
 *
 * Today's answer is the shared snapshot listener (`liveContactStore`). The record is
 * moving to Postgres (`contacts/contactDocumentSource.ts`), where this becomes a wrapper
 * over `useContactDocument`: the document store, filled by `POST /contacts/get` and
 * refreshed when the contact's signal document says a newer version exists — shared
 * between readers the same way, so the handoff keeps its first-render answer. In
 * 'shadow' mode both run, the listener answers, and the two are compared.
 *
 * The implementation is chosen once, at module load, so the 'firestore' hook is the
 * listener alone — no document store, no signal listener, nothing that needs a server
 * context — and the rules of hooks hold on every render whichever mode ships.
 *
 * `loading` is true from the first render until the first answer for THIS id — a
 * render that asks about a different id gets `loading: true`, never the previous
 * contact's answer — so `!loading && !contact` reads as "the document is gone".
 *
 * ── The book is the server's, not the email (pooled agencies, plan D4) ────────
 *
 * Under 'shadow' / 'server' the document store is keyed by `useContactBook().bookKey`:
 * the email for a personal book, the office's `org:` key for a member of a pooled
 * agency. The 'firestore' listener keeps its `dashboard-users-v2/{email}` path and is
 * NOT re-keyed — a pooled record has no Firestore document under either path, and the
 * runbook requires 'server' mode before any agency is flipped, so the listener path
 * never meets an `org:` key.
 *
 * `read.scope: "org"` asks under the agency view — what the CRM's office tab needs for a
 * pooled record the viewer does not hold (under `mine` the server narrows the office book
 * to the viewer's own list). Ignored by the 'firestore' listener.
 */
export type LiveContactReadOptions = ContactReadOptions;

export const useLiveContact: (contactId: string | null | undefined, read?: LiveContactReadOptions) => LiveContactSnapshot =
  CONTACT_DOCUMENT_SOURCE === "firestore" ? useFirestoreLiveContact : useSwitchedLiveContact;

/**
 * Today's mechanism: the shared snapshot listener on the document. `null` as the id
 * means "do not listen" — a reader that is disabled reads IDLE and holds no listener.
 */
function useFirestoreLiveContact(contactId: string | null | undefined): LiveContactSnapshot {
  const { user } = useAuth();
  const userEmail = user?.email as string | undefined;

  const [, forceRender] = useState(0);
  const snapshot = liveContacts.read(userEmail, contactId);

  useEffect(() => {
    if (!userEmail || !contactId) return undefined;
    const update = (): void => forceRender((version) => version + 1);
    const unsubscribe = liveContacts.subscribe(userEmail, contactId, update);
    // A snapshot may have arrived between render and effect (or synchronously while
    // subscribing). Close that gap without waiting for another Firestore update.
    if (liveContacts.read(userEmail, contactId) !== snapshot) update();

    return unsubscribe;
    // The identity owns the subscription; snapshot updates must not restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail, contactId]);

  return snapshot;
}

/**
 * 'shadow' and 'server': the document store runs; in 'shadow' the listener still
 * answers and the two are compared, in 'server' the listener is not even started
 * (its id is withheld) and the document store answers.
 */
function useSwitchedLiveContact(contactId: string | null | undefined, read?: LiveContactReadOptions): LiveContactSnapshot {
  const id = contactId || null;
  // STRICT: null until the server has said which book — see the header. A reader given
  // null waits (`loading`), which is one round trip on a cold browser for a pooled member.
  const { bookKey } = useContactBook();

  const listener = useFirestoreLiveContact(CONTACT_DOCUMENT_SOURCE === "server" ? null : id);
  const document = useContactDocument(bookKey, id, true, read);

  const server = useMemo<LiveContactSnapshot>(
    () =>
      !id
        ? IDLE
        : {
            contact: document.doc ?? null,
            // No answer yet and nothing failed: the first request is on its way — or the
            // book itself is (`bookKey` null), which is the same wait from here.
            loading: document.doc === undefined && !document.error,
            error: document.error
          },
    [id, document.doc, document.error]
  );

  useContactDocumentShadow(
    "useLiveContact",
    id && !listener.loading && !listener.error
      ? { exists: Boolean(listener.contact), doc: listener.contact }
      : undefined,
    id && document.doc !== undefined
      ? { exists: Boolean(document.doc), doc: document.doc, lastFetch: document.lastFetch }
      : undefined
  );

  return CONTACT_DOCUMENT_SOURCE === "server" ? server : listener;
}
